import Async from "components/DesignSystem/AsyncComponents/Async";
import {
  METRICS,
  PERIOD,
  PeriodType,
  REVENUE_BASIS,
} from "constants/revenueMetrics";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { usePageTitle } from "hooks/usePageTitle";
import { useGetAllRevenueMetricsQuery } from "store/apis/revenueMetrics";
import { formatRevenueData } from "utils/formatRevenueData";
import { FinalDataTable } from "./FinalDataTable";
import { RevenueChart } from "./RevenueChart";

import { YYYY_MM_DD } from "constants/date";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { getDateRange } from "utils/getDateRange";
import { TotalRevenueFilter } from "./RevenueFilter";
import {
  CustomizeRevenueMetricsTrigger,
  useRevenueMetricsCustomize,
} from "./CustomizeRevenueMetricsPanel";
import { PaginationHandler } from "components/DesignSystem/Pagination/Pagination";
import { useQuery } from "hooks/useQuery";
import { ACCOUNTING_METHODS } from "constants/bookkeeping";
import { AccountingMethodBanner } from "components/BooksSettings/AccountingMethodBanner";
import {
  BooksLockDateResponse,
  useGetBookkeepingSettingsQuery,
} from "store/apis/booksLockDate";
import {
  REVENUE_DATA_SOURCE,
  REVENUE_DATA_SOURCE_TYPES,
} from "constants/revenueDataSource";
import { useState } from "react";
import { TopCustomersCard } from "./TopCustomersCard";
import { CustomerGrowthCard } from "./CustomerGrowthCard";
import Loader from "components/DesignSystem/Loader/loader";

export type FilterValues = {
  START_DATE: string;
  END_DATE: string;
  SELECT_PERIOD: string;
  VIEW_TYPE: PeriodType;
  REVENUE_BASIS: keyof typeof REVENUE_BASIS;
  ACCOUNTING_METHOD: keyof typeof ACCOUNTING_METHODS;
  SOURCE: REVENUE_DATA_SOURCE_TYPES[];
};

const TotalRevenueContent = ({
  settings,
}: {
  settings: BooksLockDateResponse;
}) => {
  const { uuid: groupId } = useCurrentGroupContext();
  const entityId = useCurrentEntityId();
  const { startDate, endDate } = getDateRange("last12months");
  const query = useQuery();
  const page_num = query.get("page_num") || 1;

  const [breakdownSearch, setBreakdownSearch] = useState("");

  const { values: filterValues, updateFilter } = useFilters<FilterValues>({
    initialValue: {
      START_DATE: dayjs(startDate).format(YYYY_MM_DD),
      END_DATE: dayjs(endDate).format(YYYY_MM_DD),
      SELECT_PERIOD: "last12months",
      VIEW_TYPE: PERIOD.MONTHLY,
      REVENUE_BASIS: REVENUE_BASIS.CUSTOMER,
      ACCOUNTING_METHOD: settings?.accounting_method!,
      SOURCE: [
        REVENUE_DATA_SOURCE.HUBSPOT,
        REVENUE_DATA_SOURCE.JUSTPAID,
        REVENUE_DATA_SOURCE.MANUAL,
        REVENUE_DATA_SOURCE.STRIPE,
        REVENUE_DATA_SOURCE.BILL_COM,
        REVENUE_DATA_SOURCE.QBO_IMPORT,
      ] as REVENUE_DATA_SOURCE_TYPES[],
    },
  });

  const { visibleSections, setVisibleSections, sectionOrder, setSectionOrder } =
    useRevenueMetricsCustomize();

  const handleCustomizeSave = (
    newVisible: Parameters<typeof setVisibleSections>[0],
    newOrder: Parameters<typeof setSectionOrder>[0]
  ) => {
    setVisibleSections(newVisible);
    setSectionOrder(newOrder);
  };

  const onRevenueBasisChange = (revenueBasis: keyof typeof REVENUE_BASIS) => {
    updateFilter("REVENUE_BASIS", revenueBasis);
  };

  const isCustomerBasis = filterValues.REVENUE_BASIS === REVENUE_BASIS.CUSTOMER;

  const sharedParams = {
    groupId,
    entityId,
    period_type: filterValues.VIEW_TYPE,
    start_date: filterValues.START_DATE,
    end_date: filterValues.END_DATE,
    metric_type: METRICS.TOTAL,
    accounting_method: filterValues.ACCOUNTING_METHOD,
    source: filterValues.SOURCE,
    search: breakdownSearch,
    page_num: Number(page_num),
  };

  const {
    data: chartsData,
    isLoading: isChartDataLoading,
    isSuccess: isChartDataSuccess,
  } = useGetAllRevenueMetricsQuery(
    {
      ...sharedParams,
      revenue_basis: REVENUE_BASIS.CUSTOMER,
    },
    { skip: !groupId || !entityId || !isCustomerBasis }
  );

  const {
    data: breakdownData,
    isLoading: isBreakdownLoading,
    isFetching: isBreakdownFetching,
    isSuccess: isBreakdownSuccess,
  } = useGetAllRevenueMetricsQuery(
    {
      ...sharedParams,
      revenue_basis: filterValues.REVENUE_BASIS,
    },
    { skip: !groupId || !entityId }
  );

  const {
    periodic_total_amounts = [],
    top_customers = [],
    customer_growth = [],
  } = chartsData || {};

  const {
    customers = [],
    total_pages = 1,
    per_page = 1,
    total_count = 1,
  } = breakdownData || {};

  const { chartData } = formatRevenueData({
    data: { periodic_total_amounts, customers: [] },
  });

  return (
    <div className="t-flex t-gap-6 t-flex-col t-pb-10 t-relative t-top-0">
      <div className="t-flex t-gap-4 t-flex-col">
        <div className="t-flex t-items-center t-justify-between sm:t-flex-wrap sm:t-gap-2">
          <TotalRevenueFilter
            filterValues={filterValues}
            updateFilter={updateFilter}
          />
          <CustomizeRevenueMetricsTrigger
            visibleSections={visibleSections}
            sectionOrder={sectionOrder}
            onSave={handleCustomizeSave}
          />
        </div>
        <AccountingMethodBanner
          selectedAccountingMethod={filterValues.ACCOUNTING_METHOD}
          entityId={entityId}
        />
      </div>

      <Async.Root
        isLoading={isChartDataLoading}
        isSuccess={isChartDataSuccess}
        isEmpty={false}
      >
        <Async.Success>
          <div className="t-flex t-flex-col t-gap-6">
            {sectionOrder.map((key) => {
              if (!visibleSections[key]) return null;

              if (key === "totalRevenue") {
                return (
                  <RevenueChart
                    key={key}
                    title="Total Revenue"
                    chartData={chartData}
                  />
                );
              }

              if (key === "topCustomers") {
                return (
                  <TopCustomersCard key={key} topCustomers={top_customers} />
                );
              }

              if (key === "customerGrowth") {
                return (
                  <CustomerGrowthCard
                    key={key}
                    customerGrowth={customer_growth}
                  />
                );
              }

              if (key === "revenueBreakdown") {
                return (
                  <Async.Root
                    key={key}
                    isLoading={isBreakdownLoading}
                    isSuccess={isBreakdownSuccess}
                    isEmpty={false}
                  >
                    <Async.Success>
                      <FinalDataTable
                        finalTableData={customers}
                        onRevenueBasisChange={onRevenueBasisChange}
                        revenueBasis={filterValues.REVENUE_BASIS}
                        onSearchChange={setBreakdownSearch}
                        pagination={
                          <PaginationHandler
                            page_num={Number(page_num)}
                            per_page={per_page}
                            total_count={total_count}
                            total_pages={total_pages}
                            isLoading={isBreakdownFetching}
                            pageQueryKey="page_num"
                          />
                        }
                      />
                    </Async.Success>
                  </Async.Root>
                );
              }

              return null;
            })}
          </div>
        </Async.Success>
      </Async.Root>
    </div>
  );
};

export const TotalRevenue = () => {
  usePageTitle("Total Revenue");
  const entityId = useCurrentEntityId();
  const { data: settings, isLoading } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );
  if (isLoading) return <Loader />;

  return <TotalRevenueContent settings={settings!} />;
};
